import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { getApi } from "../../../api/api";
import {
  CategoryInterface,
  PromoInterface,
  SaleItemInterface,
} from "../../../common/types/interfaces";
import {
  selectClusterList,
  selectClusterListLoading,
} from "../../../store/slices/clusterListSlice";
import { useScrollPagination } from "../../../common/hooks/useScrollPagination";

const PAGE_LIMIT = 24;

export const useClusterDetails = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const search = searchParams.get("search") || "";
  const clusterId = Number(id);

  const clusters = useSelector(selectClusterList);
  const clustersLoading = useSelector(selectClusterListLoading);

  const [categories, setCategories] = useState<CategoryInterface[]>([]);
  const [promos, setPromos] = useState<PromoInterface[]>([]);

  // Состояние для товаров (бесконечный скролл)
  const [items, setItems] = useState<SaleItemInterface[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const cluster = clusters.find((c) => c.id === clusterId) || null;

  // Загружаем категории и акции один раз при открытии страницы
  useEffect(() => {
    if (!clusterId || clustersLoading) return;
    let cancelled = false;

    Promise.all([
      getApi().getCategories({ clusterId, rootOnly: true }),
      getApi().getPromos({ limit: 1000 }),
    ])
      .then(([catsRes, promosRes]) => {
        if (cancelled) return;
        // Фильтруем категории и акции по кластеру
        setCategories(catsRes.data.filter((c) => c.clusterId === clusterId));
        setPromos(promosRes.data.filter((p) => p.clusterId === clusterId));
      })
      .catch(() => {
        // Игнорируем ошибку категорий, чтобы страница не падала
      });

    return () => {
      cancelled = true;
    };
  }, [clusterId, clustersLoading]);

  // Сброс товаров при смене поиска
  useEffect(() => {
    setItems([]);
    setOffset(0);
    setHasMore(true);
    // setLoading(true); // Не сбрасываем loading, чтобы избежать мигания
  }, [search, clusterId]);

  // Загрузка товаров
  useEffect(() => {
    if (!clusterId || clustersLoading) return;
    if (!hasMore && offset > 0) return;
    if (isFetching || isFetchingMore) return;

    let cancelled = false;
    if (offset === 0) setIsFetching(true);
    else setIsFetchingMore(true);

    getApi()
      .getItems({
        clusterId,
        search: search || undefined,
        limit: PAGE_LIMIT,
        offset,
      })
      .then((res) => {
        if (cancelled) return;
        setItems((prev) => (offset === 0 ? res.data : [...prev, ...res.data]));
        if (res.data.length < PAGE_LIMIT) setHasMore(false);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      })
      .finally(() => {
        if (!cancelled) {
          setIsFetching(false);
          setIsFetchingMore(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [clusterId, search, offset, clustersLoading]);

  const loadMore = useCallback(() => {
    if (!isFetching && !isFetchingMore && hasMore) {
      setOffset((prev) => prev + PAGE_LIMIT);
    }
  }, [isFetching, isFetchingMore, hasMore]);

  const { lastItemRef } = useScrollPagination(
    hasMore,
    isFetchingMore,
    loadMore,
  );

  return {
    cluster,
    categories,
    promos,
    items,
    loading: clustersLoading || loading,
    isFetching,
    isFetchingMore,
    hasMore,
    loadMore,
    lastItemRef,
  };
};
